'use client';
import React from 'react';

import { cn } from '@/lib/utils';

import { useAccordion } from '@/components/molecules/accordion/AccordionProvider';

import { GOOGLE_SCHEMA, SCHEMA_LINKS } from '@/constant/schemas';

import TrackerClickWrapper from '@/components/atoms/TrackerClickWrapper';
import IconChevronDown from '~/svg/IconChevronDown.svg';

export type AccordionItemProps = {
  children: React.ReactNode;
  header: React.ReactNode;
  id: string;
  enableGoogleSchema?: boolean;
  classNames?: {
    wrapper?: string;
    headerWrapper?: string;
    icon?: string;
    contentWrapper?: string;
    content?: string;
  };
};

export default function AccordionItem({
  children,
  header,
  id,
  enableGoogleSchema,
  classNames,
}: AccordionItemProps) {
  const { openItemId, setOpenItemId } = useAccordion();

  const isOpen = id === openItemId;

  function handleToggle() {
    setOpenItemId(isOpen ? null : id);
  }

  return (
    <div
      className={cn(
        'border-neutral-N10	flex flex-col gap-6 border-t border-solid',
        classNames?.wrapper
      )}
      {...(enableGoogleSchema
        ? {
            itemScope: true,
            itemProp: GOOGLE_SCHEMA.entity,
            itemType: SCHEMA_LINKS.QUESTION,
          }
        : {})}
    >
      <div
        onClick={handleToggle}
        className={cn(
          'flex cursor-pointer items-center justify-between pt-6',
          classNames?.headerWrapper
        )}
        {...(enableGoogleSchema
          ? {
              itemProp: GOOGLE_SCHEMA.name,
            }
          : {})}
        data-tracker-component={`accordion-item-unfold-${id}`}
      >
        {header}
        <TrackerClickWrapper>
          <IconChevronDown
            className={cn(
              'text-neutral-N50 min-h-[1.5rem] min-w-[1.5rem] transition-all',
              isOpen && 'rotate-180',
              classNames?.icon
            )}
          />
        </TrackerClickWrapper>
      </div>
      <div
        className={cn(isOpen ? 'block' : 'hidden', classNames?.contentWrapper)}
        {...(enableGoogleSchema
          ? {
              itemScope: true,
              itemType: SCHEMA_LINKS.ANSWER,
              itemProp: GOOGLE_SCHEMA.answers,
            }
          : {})}
      >
        <div
          className={cn('text-neutral-N80', classNames?.content)}
          {...(enableGoogleSchema
            ? {
                itemProp: GOOGLE_SCHEMA.text,
              }
            : {})}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
